/**
 * Agent-agnostic core types. Every input adapter (Claude Code today, Codex
 * later) normalizes its native log format into TurnEvent/SideEvent, so the
 * store, analysis and report layers never touch adapter-specific shapes.
 */

export type AgentKind = "claude-code" | "codex";

export const PHASES = ["exploration", "planning", "execution", "delegation", "chat"] as const;
export type Phase = (typeof PHASES)[number];

export interface TokenUsage {
  input: number;
  output: number;
  cacheRead: number;
  /** Total cache-creation tokens (5m + 1h when a breakdown exists). */
  cacheWrite: number;
  cacheWrite5m: number;
  cacheWrite1h: number;
}

/**
 * One billable assistant turn. Privacy invariant: no message text, thinking
 * content or tool inputs — only numbers, names and identifiers.
 */
export interface TurnEvent {
  agent: AgentKind;
  uuid: string;
  /**
   * API message id. Claude Code writes one JSONL line PER CONTENT BLOCK of a
   * response, each repeating the same usage — lines sharing a messageId are
   * one billable turn and must be merged before any cost math.
   */
  messageId?: string;
  requestId?: string;
  sessionId: string;
  /** For subagent turns: the session that spawned this agent. */
  parentSessionId?: string;
  /** For subagent turns: stable agent id (matches the log filename). */
  agentId?: string;
  isSidechain: boolean;
  /** Real project root, taken from the `cwd` field of the log line. */
  projectPath: string;
  /** Human-friendly random session slug (not content-derived). */
  slug?: string;
  /** Epoch milliseconds (UTC). 0 when the line had no parseable timestamp. */
  timestamp: number;
  model: string;
  usage: TokenUsage;
  /** tool_use block names, in order of appearance. */
  tools: string[];
  numToolUses: number;
  thinkingChars: number;
  textChars: number;
  stopReason?: string;
  gitBranch?: string;
  cliVersion?: string;
  /** usage.speed — 'fast' turns bill at fast-mode premium rates. */
  speed?: string;
  /** usage.inference_geo — 'us' applies a 1.1x pricing multiplier. */
  inferenceGeo?: string;
  filePath: string;
}

export type SideEventKind =
  | "plan_mode"
  | "plan_mode_exit"
  | "compact_boundary"
  | "turn_duration"
  | "agent_result";

/**
 * Non-turn signal extracted from the logs (plan-mode boundaries, compaction,
 * subagent result summaries). `data` holds a whitelisted, content-free payload.
 */
export interface SideEvent {
  kind: SideEventKind;
  sessionId: string;
  timestamp?: number;
  filePath: string;
  data: Record<string, unknown>;
}

export type LogFileKind = "session" | "subagent" | "other";

export interface DiscoveredFile {
  path: string;
  kind: LogFileKind;
  sizeBytes: number;
  /** Truncated to integer ms so equality checks match the stored value. */
  mtimeMs: number;
  sessionId?: string;
  agentId?: string;
  parentSessionId?: string;
}

export interface ParseBatch {
  turns: TurnEvent[];
  events: SideEvent[];
  /**
   * Absolute byte offset after the last newline-terminated line. Safe resume
   * point: an unterminated trailing line is never included here.
   */
  bytesConsumed: number;
  linesParsed: number;
  parseErrors: number;
  unknownTypes: Record<string, number>;
}

export interface AgentAdapter {
  readonly kind: AgentKind;
  detect(rootOverride?: string): { found: boolean; rootDirs: string[] };
  listLogFiles(rootDirs: string[]): DiscoveredFile[];
  parseFile(file: DiscoveredFile, fromOffset: number): AsyncIterable<ParseBatch>;
}
